import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { WriteQuestionnaire } from "@/types/questionnaire";
import { useQuestionnaireContext } from "../context/QuestionnaireContext";
import { useFilePreview } from "@/components/file-preview/hooks";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";
import type { StoredFile } from "@/lib/file-storage/types";
import {
  AttachmentItem,
  QuestionnaireAttachmentMeta,
  useQuestionnaireAttachmentUpload,
  useQuestionnaireAttachmentDeletion,
} from "./attachment";
import { mergeAttachmentMeta, toAttachmentFormValue } from "../utils";
import { mapArrayByKey } from "@/lib/mapArrayByKey";
import type { AttachmentFieldName } from "../types/attachments";

export const useQuestionnaireAttachmentField = (
  name: AttachmentFieldName,
  existingAttachments: QuestionnaireAttachmentMeta[] = [],
) => {
  const { control } = useFormContext<WriteQuestionnaire>();
  const { presignedClient } = useQuestionnaireContext();
  const { formatFileSize } = useFormatters();

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const [uploadMeta, setUploadMeta] = useState<
    Map<string, { size: string; confirmed_at?: string | null }>
  >(new Map());

  const onFileUploaded = useCallback(
    (file: File, stored: StoredFile) => {
      append({ filename: stored.filename, s3_key: stored.s3Key });
      setUploadMeta((prev) => {
        const next = new Map(prev);
        next.set(stored.s3Key, {
          size: formatFileSize(file.size),
          confirmed_at: stored.metadata?.confirmed_at ?? null,
        });
        return next;
      });
    },
    [append, formatFileSize],
  );

  const onFileDeleted = useCallback(
    (s3Key: string, index: number) => {
      remove(index);
      setUploadMeta((prev) => {
        const next = new Map(prev);
        next.delete(s3Key);
        return next;
      });
    },
    [remove],
  );

  const existingMetaByKey = useMemo(
    () => mapArrayByKey(existingAttachments, "s3Key"),
    [existingAttachments],
  );

  const attachmentItems = useMemo<AttachmentItem[]>(
    () =>
      fields.map((field, index) => {
        const attachment = toAttachmentFormValue(field);
        const existingMeta = existingMetaByKey[attachment.s3_key];
        const pendingMeta = uploadMeta.get(attachment.s3_key);
        const meta = mergeAttachmentMeta(existingMeta, pendingMeta, {
          filename: attachment.filename,
          s3Key: attachment.s3_key,
        });
        const key = attachment.id || attachment.s3_key;
        const url = buildFileUrl(attachment.s3_key);
        return {
          index,
          key,
          field: attachment,
          meta,
          url,
        };
      }),
    [fields, existingMetaByKey, uploadMeta],
  );

  const {
    preview,
    expandedKeys,
    toggleInlinePreview,
    openPreviewDialog,
    setPreview,
    closePreview,
  } = useFilePreview({
    items: attachmentItems,
  });

  const {
    fileInputRef,
    operationProgress,
    pendingUpload,
    confirmUpload,
    cancelUpload,
    handleAddFilesClick,
    handleFileUpload,
    handleDrop,
  } = useQuestionnaireAttachmentUpload({
    name,
    presignedClient,
    onFileUploaded,
  });

  const {
    pendingDelete,
    confirmDelete,
    cancelDelete,
    handleRemove,
    isDeleting,
    operationProgress: deleteOperationProgress,
  } = useQuestionnaireAttachmentDeletion({
    presignedClient,
    name,
    onFileDeleted,
  });

  const isProcessing = operationProgress.isBusy() || isDeleting;

  return {
    fileInputRef,
    isProcessing,
    operationProgress,
    deleteOperationProgress,
    attachmentItems,
    expandedKeys,
    preview,
    handleAddFilesClick,
    handleFileUpload,
    handleDrop,
    handleRemove,
    toggleInlinePreview,
    openPreviewDialog,
    setPreview,
    closePreview,
    pendingDelete,
    confirmDelete,
    cancelDelete,
    pendingUpload,
    confirmUpload,
    cancelUpload,
  };
};
